import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripDotsVertical,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import { faSquarePlus, faSquareMinus } from "@fortawesome/pro-light-svg-icons";
import { BpPhase, BpService, BpSubservice } from "./types";

const COL_SPAN = 4;

interface ProjectResource {
  id: string;
  name: string;
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  serviceType: "professional_services" | "managed_services";
  phases: BpPhase[];
  services: BpService[];
  setServices: (services: BpService[]) => void;
  projectResources: ProjectResource[];
  readOnly?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const normalizePositions = (services: BpService[]): BpService[] => {
  const byPhase: Record<string, BpService[]> = {};
  services.forEach((s) => {
    const key = s.phaseId ?? "_flat";
    if (!byPhase[key]) byPhase[key] = [];
    byPhase[key].push(s);
  });
  return Object.values(byPhase).flatMap((group) =>
    group.map((s, i) => ({ ...s, position: i + 1 }))
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const BlueprintServiceEditor: React.FC<Props> = ({
  serviceType,
  phases,
  services,
  setServices,
  projectResources,
  readOnly,
}) => {
  const isProfessional = serviceType === "professional_services";
  const [activeService, setActiveService] = useState<BpService | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedServices, setExpandedServices] = useState<
    Record<string, boolean>
  >({});

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id.toString().replace("service:", "");
    setActiveService(services.find((s) => s.id === id) ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveService(null);
    if (!over || active.id === over.id) return;

    const activeId = active.id.toString().replace("service:", "");
    const overId = over.id.toString();

    let updated = [...services];

    if (overId.startsWith("phase-drop:")) {
      const targetPhaseId = overId.replace("phase-drop:", "");
      updated = services.map((s) =>
        s.id === activeId ? { ...s, phaseId: targetPhaseId, position: 9999 } : s
      );
    } else if (overId.startsWith("service:")) {
      const overServiceId = overId.replace("service:", "");
      const activeIdx = services.findIndex((s) => s.id === activeId);
      const overIdx = services.findIndex((s) => s.id === overServiceId);
      if (activeIdx === -1 || overIdx === -1) return;

      const overService = services[overIdx];
      const activeServiceEntry = services[activeIdx];
      updated = arrayMove(services, activeIdx, overIdx);

      if (activeServiceEntry.phaseId !== overService.phaseId) {
        updated = updated.map((s) =>
          s.id === activeId ? { ...s, phaseId: overService.phaseId } : s
        );
      }
    } else {
      return;
    }

    setServices(normalizePositions(updated));
  };

  const handleSelect = (id: string, checked: boolean) => {
    setServices(services.map((s) => (s.id === id ? { ...s, checked } : s)));
  };

  const handleUpdateService = (id: string, changes: Partial<BpService>) => {
    setServices(services.map((s) => (s.id === id ? { ...s, ...changes } : s)));
  };

  const handleUpdateSubservice = (
    serviceId: string,
    subserviceId: string,
    changes: Partial<BpSubservice>
  ) => {
    setServices(
      services.map((s) =>
        s.id === serviceId
          ? {
              ...s,
              subservices: s.subservices.map((sub) =>
                sub.id === subserviceId ? { ...sub, ...changes } : sub
              ),
            }
          : s
      )
    );
  };

  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => ({
      ...prev,
      [phaseId]: !(prev[phaseId] ?? true),
    }));
  };

  const toggleService = (serviceId: string) => {
    setExpandedServices((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  const sortedServices = [...services].sort((a, b) => a.position - b.position);

  const phaseServices = (phaseId: string) =>
    services
      .filter((s) => s.phaseId === phaseId)
      .sort((a, b) => a.position - b.position);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {isProfessional ? (
        phases.map((phase) => (
          <PhaseCard
            key={phase.id}
            phase={phase}
            services={phaseServices(phase.id)}
            isExpanded={expandedPhases[phase.id] ?? true}
            onTogglePhase={() => togglePhase(phase.id)}
            expandedServices={expandedServices}
            onToggleService={toggleService}
            onSelect={handleSelect}
            onUpdateService={handleUpdateService}
            onUpdateSubservice={handleUpdateSubservice}
            projectResources={projectResources}
            readOnly={readOnly}
          />
        ))
      ) : (
        <SortableContext
          items={sortedServices.map((s) => `service:${s.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <ServiceTable
            services={sortedServices}
            expandedServices={expandedServices}
            onToggleService={toggleService}
            onSelect={handleSelect}
            onUpdateService={handleUpdateService}
            onUpdateSubservice={handleUpdateSubservice}
            projectResources={projectResources}
            readOnly={readOnly}
          />
        </SortableContext>
      )}

      <DragOverlay adjustScale={false}>
        {activeService ? (
          <table className="serviceTable">
            <tbody>
              <tr>
                <td colSpan={COL_SPAN}>
                  <div className="serviceTd">
                    <span className="dndHandle">
                      <FontAwesomeIcon icon={faGripDotsVertical} />
                    </span>
                    {activeService.name}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

// ── ServiceTable ───────────────────────────────────────────────────────────────
// Shared table shell used by both the managed-services flat view and each PhaseCard.

interface ServiceTableProps {
  services: BpService[];
  expandedServices: Record<string, boolean>;
  onToggleService: (id: string) => void;
  onSelect: (id: string, checked: boolean) => void;
  onUpdateService: (id: string, changes: Partial<BpService>) => void;
  onUpdateSubservice: (
    serviceId: string,
    subserviceId: string,
    changes: Partial<BpSubservice>
  ) => void;
  projectResources: ProjectResource[];
  readOnly?: boolean;
}

const ServiceTable: React.FC<ServiceTableProps> = ({
  services,
  expandedServices,
  onToggleService,
  onSelect,
  onUpdateService,
  onUpdateSubservice,
  projectResources,
  readOnly,
}) => (
  <table className="serviceTable">
    <thead>
      <tr>
        <th className="nameCol">Service Name</th>
        <th className="qtyCol">Qty</th>
        <th className="hoursCol">Hrs</th>
        <th className="resourceCol">Resource</th>
      </tr>
    </thead>
    <tbody>
      {services.map((service, i) => (
        <SortableServiceRow
          key={service.id}
          service={service}
          striped={i % 2 === 1}
          isExpanded={!!expandedServices[service.id]}
          onToggleExpand={() => onToggleService(service.id)}
          onSelect={onSelect}
          onUpdate={(changes) => onUpdateService(service.id, changes)}
          onUpdateSubservice={(subId, changes) =>
            onUpdateSubservice(service.id, subId, changes)
          }
          projectResources={projectResources}
          readOnly={readOnly}
        />
      ))}
    </tbody>
  </table>
);

// ── PhaseCard ──────────────────────────────────────────────────────────────────

interface PhaseCardProps {
  phase: BpPhase;
  services: BpService[];
  isExpanded: boolean;
  onTogglePhase: () => void;
  expandedServices: Record<string, boolean>;
  onToggleService: (id: string) => void;
  onSelect: (id: string, checked: boolean) => void;
  onUpdateService: (id: string, changes: Partial<BpService>) => void;
  onUpdateSubservice: (
    serviceId: string,
    subserviceId: string,
    changes: Partial<BpSubservice>
  ) => void;
  projectResources: ProjectResource[];
  readOnly?: boolean;
}

const PhaseCard: React.FC<PhaseCardProps> = ({
  phase,
  services,
  isExpanded,
  onTogglePhase,
  expandedServices,
  onToggleService,
  onSelect,
  onUpdateService,
  onUpdateSubservice,
  projectResources,
  readOnly,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: `phase-drop:${phase.id}` });

  return (
    <div className="projectPhase">
      <div className="phaseHeader">
        <span className="phaseTitle">{phase.name}</span>
        <FontAwesomeIcon
          className="expandIcon"
          icon={isExpanded ? faChevronUp : faChevronDown}
          onClick={onTogglePhase}
        />
      </div>

      {isExpanded && (
        <div
          ref={setNodeRef}
          className={`phaseBody${
            isOver && services.length === 0 ? " dropOver" : ""
          }`}
        >
          {services.length === 0 ? (
            <div className="placeholder">
              No services assigned to this phase
            </div>
          ) : (
            <SortableContext
              items={services.map((s) => `service:${s.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <ServiceTable
                services={services}
                expandedServices={expandedServices}
                onToggleService={onToggleService}
                onSelect={onSelect}
                onUpdateService={onUpdateService}
                onUpdateSubservice={onUpdateSubservice}
                projectResources={projectResources}
                readOnly={readOnly}
              />
            </SortableContext>
          )}
        </div>
      )}
    </div>
  );
};

// ── SortableServiceRow ─────────────────────────────────────────────────────────

interface ServiceRowProps {
  service: BpService;
  striped: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelect: (id: string, checked: boolean) => void;
  onUpdate: (changes: Partial<BpService>) => void;
  onUpdateSubservice: (
    subserviceId: string,
    changes: Partial<BpSubservice>
  ) => void;
  projectResources: ProjectResource[];
  readOnly?: boolean;
}

const SortableServiceRow: React.FC<ServiceRowProps> = ({
  service,
  striped,
  isExpanded,
  onToggleExpand,
  onSelect,
  onUpdate,
  onUpdateSubservice,
  projectResources,
  readOnly,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `service:${service.id}` });

  const bg = striped ? "#FAFAFA" : "#fff";

  return (
    <React.Fragment>
      <tr
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          backgroundColor: bg,
        }}
      >
        <td className="nameCol">
          <div className="serviceTd">
            <span
              {...(!readOnly ? attributes : {})}
              {...(!readOnly ? listeners : {})}
              className="dndHandle"
            >
              <FontAwesomeIcon icon={faGripDotsVertical} />
            </span>
            <input
              type="checkbox"
              style={{ accentColor: "#418172" }}
              checked={!!service.checked}
              disabled={readOnly}
              onChange={(e) => onSelect(service.id, e.target.checked)}
            />
            <FontAwesomeIcon
              icon={isExpanded ? faSquareMinus : faSquarePlus}
              size="lg"
              className="serviceToggle"
              onClick={() => {
                const scrollY = window.scrollY;
                requestAnimationFrame(() =>
                  window.scrollTo({ top: scrollY, behavior: "auto" })
                );
                onToggleExpand();
              }}
            />
            <span>{service.name}</span>
          </div>
        </td>
        <td className="qtyCol">
          <div className="serviceTd">
            <Form.Control
              type="number"
              min={0}
              step={1}
              defaultValue={service.quantity ?? 1}
              disabled={readOnly}
              onChange={(e) => onUpdate({ quantity: Number(e.target.value) })}
            />
          </div>
        </td>
        <td className="hoursCol">
          <div className="serviceTd">
            <Form.Control
              type="number"
              min={0}
              step={0.25}
              defaultValue={service.hours}
              disabled={readOnly}
              onChange={(e) => onUpdate({ hours: Number(e.target.value) })}
            />
          </div>
        </td>
        <td className="resourceCol">
          <div className="serviceTd">
            <Select
              value={
                service.resourceId
                  ? projectResources
                      .filter((r) => r.id === service.resourceId)
                      .map((r) => ({ value: r.id, label: r.name }))[0] ?? null
                  : null
              }
              options={projectResources.map((r) => ({
                value: r.id,
                label: r.name,
              }))}
              onChange={(opt) => onUpdate({ resourceId: opt?.value ?? null })}
              isClearable
              isDisabled={readOnly}
              placeholder="None"
              styles={{ container: (base) => ({ ...base, width: "100%" }) }}
            />
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className={`subservicesRow${striped ? " striped" : ""}`}>
          <td colSpan={COL_SPAN}>
            <div className="subservices">
              {service.subservices.length > 0 ? (
                <SubserviceTable
                  subservices={service.subservices}
                  projectResources={projectResources}
                  onUpdate={onUpdateSubservice}
                  readOnly={readOnly}
                />
              ) : (
                <div className="bpPlaceholder">No subservices.</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// ── SubserviceTable ────────────────────────────────────────────────────────────

interface SubserviceTableProps {
  subservices: BpSubservice[];
  projectResources: ProjectResource[];
  onUpdate: (subserviceId: string, changes: Partial<BpSubservice>) => void;
  readOnly?: boolean;
}

const SubserviceTable: React.FC<SubserviceTableProps> = ({
  subservices,
  projectResources,
  onUpdate,
  readOnly,
}) => (
  <table className="serviceTable subservices">
    <thead>
      <tr>
        <th className="nameCol">Subservice Name</th>
        <th className="qtyCol">Qty</th>
        <th className="hoursCol">Hrs</th>
        <th className="resourceCol">Resource</th>
      </tr>
    </thead>
    <tbody>
      {subservices.map((sub, i) => (
        <tr
          key={sub.id}
          style={{ backgroundColor: i % 2 === 1 ? "#FAFAFA" : "#fff" }}
        >
          <td className="nameCol">
            <div className="serviceTd">{sub.name}</div>
          </td>
          <td className="qtyCol">
            <div className="serviceTd">
              <Form.Control
                type="number"
                min={0}
                step={1}
                defaultValue={sub.quantity}
                disabled={readOnly}
                onChange={(e) =>
                  onUpdate(sub.id, { quantity: Number(e.target.value || 0) })
                }
              />
            </div>
          </td>
          <td className="hoursCol">
            <div className="serviceTd">
              <Form.Control
                type="number"
                min={0}
                step={0.25}
                defaultValue={sub.hours}
                disabled={readOnly}
                onChange={(e) =>
                  onUpdate(sub.id, { hours: Number(e.target.value || 0) })
                }
              />
            </div>
          </td>
          <td className="resourceCol">
            <div className="serviceTd">
              <Select
                value={
                  sub.resourceId
                    ? projectResources
                        .filter((r) => r.id === sub.resourceId)
                        .map((r) => ({ value: r.id, label: r.name }))[0] ?? null
                    : null
                }
                options={projectResources.map((r) => ({
                  value: r.id,
                  label: r.name,
                }))}
                onChange={(opt) =>
                  onUpdate(sub.id, { resourceId: opt?.value ?? null })
                }
                isClearable
                isDisabled={readOnly}
                placeholder="None"
                styles={{ container: (base) => ({ ...base, width: "100%" }) }}
              />
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default BlueprintServiceEditor;
